import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Paper, CircularProgress } from '@mui/material';
import { PlayArrow } from '@mui/icons-material';
import { useSettings } from '@/context/SettingsContext';

interface StartScreenProps {
  onStart: () => void;
  token: string;
}

interface SurveyInfo {
  title: string;
  description: string;
  language: string;
}

const StartScreen: React.FC<StartScreenProps> = ({ onStart, token }) => {
  const { language } = useSettings();
  const [surveyInfo, setSurveyInfo] = useState<SurveyInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // If no token provided, show not found error
    if (!token) {
      setError('not_found');
      setLoading(false);
      return;
    }

    const fetchSurveyInfo = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/sessions/survey/${token}`);
        if (response.ok) {
          const data = await response.json();
          setSurveyInfo(data);
          setError(null);
        } else {
          if (response.status === 404) {
            setError('not_found');
          } else {
            setError('error');
          }
          console.error('Failed to fetch survey info');
        }
      } catch (error) {
        console.error('Error fetching survey info:', error);
        setError('error');
      } finally {
        setLoading(false);
      }
    };

    fetchSurveyInfo();
  }, [token]);

  const translations = {
    en: {
      greeting: 'Dear Customer, tell us what you think',
      startButton: 'Start Survey',
      loading: 'Loading survey...',
      notFoundTitle: 'Survey Not Found',
      notFoundMessage: 'The survey you are looking for does not exist or is not active. Please check your survey link and try again.',
      errorTitle: 'Error Loading Survey',
      errorMessage: 'Unable to load the survey. Please try again later or contact support if the problem persists.',
    },
    ar: {
      greeting: 'عميلنا العزيز، أخبرنا برأيك',
      startButton: 'ابدأ الاستبيان',
      loading: 'جاري تحميل الاستبيان...',
      notFoundTitle: 'الاستبيان غير موجود',
      notFoundMessage: 'الاستبيان الذي تبحث عنه غير موجود أو غير نشط. يرجى التحقق من رابط الاستبيان والمحاولة مرة أخرى.',
      errorTitle: 'خطأ في تحميل الاستبيان',
      errorMessage: 'تعذر تحميل الاستبيان. يرجى المحاولة مرة أخرى لاحقاً أو الاتصال بالدعم إذا استمرت المشكلة.',
    },
  };

  const t = translations[language];

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography>{t.loading}</Typography>
      </Box>
    );
  }

  // Show error message if survey not found or error occurred
  if (error) {
    const errorTitle = error === 'not_found' ? t.notFoundTitle : t.errorTitle;
    const errorMessage = error === 'not_found' ? t.notFoundMessage : t.errorMessage;

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          gap: 3,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 6,
            textAlign: 'center',
            maxWidth: 600,
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ mb: 2, fontWeight: 'bold', color: 'error.main' }}>
            {errorTitle}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
            {errorMessage}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        gap: 3,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 6,
          textAlign: 'center',
          maxWidth: 600,
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
          {t.greeting}
        </Typography>
        {surveyInfo?.title && (
          <Typography variant="h6" gutterBottom sx={{ mb: 2, color: 'text.primary' }}>
            {surveyInfo.title}
          </Typography>
        )}
        {surveyInfo?.description && (
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.8 }}>
            {surveyInfo.description}
          </Typography>
        )}
        <Button
          variant="contained"
          size="large"
          startIcon={<PlayArrow />}
          onClick={onStart}
          sx={{ px: 6, py: 2 }}
        >
          {t.startButton}
        </Button>
      </Paper>
    </Box>
  );
};

export default StartScreen;
